// socialUrlUtils.js

const PROFILE_URL_PATTERNS = {
  instagramProfile:
    /^(?:https?:\/\/)?(?:www\.|m\.)?instagram\.com\/(?!(?:p|reel|reels|tv|stories|s)\/)(?<username>[A-Za-z0-9._]+)\/?(?:\?.*)?$/i,

  xOrTwitterProfile:
    /^(?:https?:\/\/)?(?:www\.|mobile\.)?(?:x\.com|twitter\.com)\/(?!(?:home|i|share|intent)\/)(?<username>[A-Za-z0-9_]+)\/?(?:\?.*)?$/i,

  twitterIntentUser:
    /^(?:https?:\/\/)?(?:www\.|mobile\.)?twitter\.com\/intent\/user\/?(?:\?.*?\bscreen_name=(?<username>[A-Za-z0-9_]+).*)$/i,

  youtubeHandleProfile:
    /^(?:https?:\/\/)?(?:www\.)?youtube\.com\/@(?<handle>[A-Za-z0-9._-]+)(?:\/(?:(?:videos|featured|shorts|streams|about))\/?)?(?:\?.*)?$/i,

  youtubeAnyChannelOrHandle:
    /^(?:https?:\/\/)?(?:www\.)?youtube\.com\/(?:(?:@(?<handle>[A-Za-z0-9._-]+))|channel\/(?<channelId>UC[A-Za-z0-9_-]+)|c\/(?<customName>[A-Za-z0-9_-]+)|user\/(?<legacyUser>[A-Za-z0-9_-]+))(?:\/(?:(?:videos|featured|shorts|streams|about))\/?)?(?:\?.*)?$/i,

  linkedInPersonProfile:
    /^(?:https?:\/\/)?(?:[\w]+\.)?linkedin\.com\/in\/(?<publicSlug>[A-Za-z0-9-_%]+)(?:\/.*)?$/i,

  linkedInCompanyProfile:
    /^(?:https?:\/\/)?(?:[\w]+\.)?linkedin\.com\/company\/(?<companySlug>[A-Za-z0-9-_%]+)(?:\/.*)?$/i,

  facebookProfileOrId:
    /^(?:https?:\/\/)?(?:www\.|m\.)?(?:facebook\.com|fb\.com)\/(?:(?:profile\.php\?id=(?<numericId>\d+))|(?!(?:pages|groups|watch|marketplace|events|gaming)\/)(?<vanityName>[A-Za-z0-9.-]+))\/?(?:\?.*)?$/i,

  tiktokProfileOrContent:
    /^(?:https?:\/\/)?(?:www\.|m\.)?tiktok\.com\/@(?<username>[A-Za-z0-9._]+)(?:\/(?:(?:video|photo)\/\d+|live)\/?)?(?:\?.*)?$/i,

  threadsProfileOrContent:
    /^(?:https?:\/\/)?(?:www\.)?threads\.net\/@(?<username>[A-Za-z0-9._]+)(?:\/(?:post\/[A-Za-z0-9_-]+)\/?)?(?:\?.*)?$/i,
};

function toTrimmedString(value) {
  return String(value ?? "").trim();
}

function ensureProtocol(maybeUrl) {
  const s = toTrimmedString(maybeUrl);
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  return `https://${s}`;
}

function getHostnameNormalized(url) {
  const u = new URL(ensureProtocol(url));
  return u.hostname
    .toLowerCase()
    .replace(/^www\./, "")
    .replace(/^m\./, "")
    .replace(/^mobile\./, "");
}

function asAtHandle(value) {
  const v = toTrimmedString(value);
  if (!v) return null;
  return v.startsWith("@") ? v : `@${v}`;
}

export function extractHandleFromSocialUrl(profileUrl) {
  const raw = toTrimmedString(profileUrl);
  if (!raw) return null;

  let host;
  let normalized;
  try {
    normalized = ensureProtocol(raw);
    host = getHostnameNormalized(raw);
  } catch {
    return null;
  }

  if (host === "instagram.com") {
    const m = normalized.match(PROFILE_URL_PATTERNS.instagramProfile);
    return asAtHandle(m?.groups?.username);
  }

  if (host === "x.com" || host === "twitter.com") {
    let m = normalized.match(PROFILE_URL_PATTERNS.xOrTwitterProfile);
    if (m?.groups?.username) return asAtHandle(m.groups.username);

    m = normalized.match(PROFILE_URL_PATTERNS.twitterIntentUser);
    return asAtHandle(m?.groups?.username);
  }

  if (host === "youtube.com") {
    let m = normalized.match(PROFILE_URL_PATTERNS.youtubeHandleProfile);
    if (m?.groups?.handle) return asAtHandle(m.groups.handle);

    m = normalized.match(PROFILE_URL_PATTERNS.youtubeAnyChannelOrHandle);
    if (!m?.groups) return null;

    const { handle, customName, legacyUser, channelId } = m.groups;

    if (handle) return asAtHandle(handle);
    if (customName) return asAtHandle(customName);
    if (legacyUser) return asAtHandle(legacyUser);
    return channelId || null;
  }

  if (host === "linkedin.com") {
    let m = normalized.match(PROFILE_URL_PATTERNS.linkedInPersonProfile);
    if (m?.groups?.publicSlug) return asAtHandle(m.groups.publicSlug);

    m = normalized.match(PROFILE_URL_PATTERNS.linkedInCompanyProfile);
    if (m?.groups?.companySlug) return asAtHandle(m.groups.companySlug);

    return null;
  }

  if (host === "facebook.com" || host === "fb.com") {
    const m = normalized.match(PROFILE_URL_PATTERNS.facebookProfileOrId);
    if (!m?.groups) return null;

    const { vanityName, numericId } = m.groups;
    return asAtHandle(vanityName || numericId);
  }

  if (host === "tiktok.com") {
    const m = normalized.match(PROFILE_URL_PATTERNS.tiktokProfileOrContent);
    return asAtHandle(m?.groups?.username);
  }

  if (host === "threads.net") {
    const m = normalized.match(PROFILE_URL_PATTERNS.threadsProfileOrContent);
    return asAtHandle(m?.groups?.username);
  }

  return null;
}

export function getSocialPlatformFromUrl(profileUrl) {
  const raw = toTrimmedString(profileUrl);
  if (!raw) return null;

  let host;
  try {
    host = getHostnameNormalized(raw);
  } catch {
    return null;
  }

  if (host === "instagram.com") return "instagram";
  if (host === "x.com" || host === "twitter.com") return "x";
  if (host === "youtube.com") return "youtube";
  if (host === "linkedin.com") return "linkedIn";
  if (host === "facebook.com" || host === "fb.com") return "facebook";
  if (host === "tiktok.com") return "tiktok";
  if (host === "threads.net") return "threads";

  return null;
}
